/**
 * Command registry — define os comandos disponíveis no Cmd+K palette.
 *
 * Cada comando tem:
 *   - id: único
 *   - labelKey: chave i18n
 *   - icon: SVG path
 *   - shortcut?: tecla atalho ("U" pra criar usuário)
 *   - keywords?: termos extras pra busca (ex: ["create", "novo"])
 *   - section?: agrupa visualmente ("create", "navigate", "settings")
 *   - visible?: predicate baseado no contexto (role, etc)
 *   - perform: função que executa
 *
 * Pra adicionar comandos novos: edita o array COMMANDS abaixo.
 * Pra registrar comandos contextuais (de uma página específica), use o hook
 * useCommand() em lib/hooks-commands.ts (TODO em implementação futura).
 */

export interface CommandContext {
  /** Função pra navegar (router.push) */
  navigate:    (path: string) => void;
  /** Role do user atual */
  role:        string;
  /** Setters de tema/locale (vindos do ThemeProvider) */
  setTheme:    (t: "light" | "dark" | "system" | "auto") => void;
  setLocale:   (l: "pt" | "en" | "es") => void;
}

export interface Command {
  id:        string;
  labelKey:  string;
  icon:      string;       // SVG path d
  shortcut?: string;       // tecla simples ("U", "?", etc) — sem prefixo
  keywords?: string[];
  section?:  "create" | "navigate" | "settings" | "help";
  visible?:  (ctx: CommandContext) => boolean;
  perform:   (ctx: CommandContext) => void;
}


// ════════════════════════════════════════════════════════════════════════════
// Comandos default da plataforma
// ════════════════════════════════════════════════════════════════════════════
export const COMMANDS: Command[] = [
  // ─── Criar ───
  {
    id: "create-user",
    labelKey: "commands.createUser",
    icon: "M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2m14-7a4 4 0 100-8 4 4 0 000 8zm6 3v6m3-3h-6",
    shortcut: "u",
    keywords: ["convidar", "invite", "novo usuário"],
    section: "create",
    visible: (ctx) => ["admin", "superadmin", "platform_admin"].includes(ctx.role),
    perform: (ctx) => ctx.navigate("/admin?action=invite"),
  },
  {
    id: "create-comunicado",
    labelKey: "commands.createComunicado",
    icon: "M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z",
    shortcut: "c",
    keywords: ["broadcast", "novo comunicado", "post"],
    section: "create",
    visible: (ctx) => ctx.role === "platform_admin",
    perform: (ctx) => ctx.navigate("/comunicados?action=new"),
  },
  {
    id: "create-empresa",
    labelKey: "commands.createEmpresa",
    icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5",
    shortcut: "e",
    keywords: ["nova empresa", "tenant", "company"],
    section: "create",
    visible: (ctx) => ctx.role === "platform_admin",
    perform: (ctx) => ctx.navigate("/platform?action=new-empresa"),
  },

  // ─── Navegar ───
  { id: "go-home",         labelKey: "commands.goHome",         icon: "m3 12 9-9 9 9-9 9z M9 21V12h6v9", section: "navigate", perform: (ctx) => ctx.navigate("/app") },
  { id: "go-comunicados",  labelKey: "commands.goComunicados",  icon: "M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z", section: "navigate", perform: (ctx) => ctx.navigate("/comunicados") },
  { id: "go-notificacoes", labelKey: "commands.goNotificacoes", icon: "M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9 M13.73 21a2 2 0 01-3.46 0", section: "navigate", perform: (ctx) => ctx.navigate("/notificacoes") },
  { id: "go-perfil",       labelKey: "commands.goPerfil",       icon: "M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2 M12 11a4 4 0 100-8 4 4 0 000 8z", section: "navigate", perform: (ctx) => ctx.navigate("/perfil") },
  { id: "go-seguranca",    labelKey: "commands.goSeguranca",    icon: "M9 12l2 2 4-4 M21 12c0 4.97-3.5 9-9 9s-9-4.03-9-9 3.5-9 9-9 9 4.03 9 9z", section: "navigate", keywords: ["2fa","mfa","autenticação","security","two-factor"], perform: (ctx) => ctx.navigate("/perfil/seguranca") },
  { id: "go-agents",       labelKey: "commands.goAgents",       icon: "M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z", section: "navigate", perform: (ctx) => ctx.navigate("/agents") },
  { id: "go-status",       labelKey: "commands.goStatus",       icon: "M4 14h6v6 M14 4h6v6 M4 4h16v16H4z", section: "navigate", keywords: ["uptime","saúde","health","sistema","servidor"], perform: (ctx) => ctx.navigate("/status") },
  { id: "go-ai-usage",     labelKey: "commands.goAiUsage",      icon: "M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z", section: "navigate", keywords: ["ia","ai","openai","custo","tokens","agents","gpt"], perform: (ctx) => ctx.navigate("/platform/ai-usage") },
  {
    id: "go-admin",
    labelKey: "commands.goAdmin",
    icon: "M9 12l2 2 4-4 M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z",
    section: "navigate",
    visible: (ctx) => ["admin", "superadmin", "platform_admin"].includes(ctx.role),
    perform: (ctx) => ctx.navigate("/admin"),
  },
  {
    id: "go-platform",
    labelKey: "commands.goPlatform",
    icon: "M3.75 21h16.5 M4.5 3h15 M5.25 3v18m13.5-18v18 M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15",
    section: "navigate",
    visible: (ctx) => ctx.role === "platform_admin",
    perform: (ctx) => ctx.navigate("/platform"),
  },

  // ─── Configurações ───
  { id: "theme-dark",   labelKey: "commands.themeDark",   icon: "M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z", section: "settings", keywords: ["escuro", "noite"], perform: (ctx) => ctx.setTheme("dark") },
  { id: "theme-light",  labelKey: "commands.themeLight",  icon: "M12 3v2 M12 19v2 M5.64 5.64l1.41 1.41 M16.95 16.95l1.41 1.41 M3 12h2 M19 12h2 M5.64 18.36l1.41-1.41 M16.95 7.05l1.41-1.41 M12 8a4 4 0 100 8 4 4 0 000-8z", section: "settings", keywords: ["claro", "dia"], perform: (ctx) => ctx.setTheme("light") },
  { id: "theme-system", labelKey: "commands.themeSystem", icon: "M2 3h20v14H2z M8 21h8 M12 17v4", section: "settings", keywords: ["sistema", "auto"], perform: (ctx) => ctx.setTheme("system") },

  { id: "lang-pt", labelKey: "commands.langPt", icon: "M2 12h20 M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z M12 2a10 10 0 100 20 10 10 0 000-20z", section: "settings", keywords: ["português", "brasil"], perform: (ctx) => ctx.setLocale("pt") },
  { id: "lang-en", labelKey: "commands.langEn", icon: "M2 12h20 M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z M12 2a10 10 0 100 20 10 10 0 000-20z", section: "settings", keywords: ["english", "inglês"],     perform: (ctx) => ctx.setLocale("en") },
  { id: "lang-es", labelKey: "commands.langEs", icon: "M2 12h20 M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z M12 2a10 10 0 100 20 10 10 0 000-20z", section: "settings", keywords: ["español", "espanhol"],   perform: (ctx) => ctx.setLocale("es") },

  // ─── Help ───
  {
    id: "help-shortcuts",
    labelKey: "commands.helpShortcuts",
    icon: "M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3 M12 17h.01 M12 21a9 9 0 110-18 9 9 0 010 18z",
    shortcut: "?",
    section: "help",
    perform: () => {
      alert("Atalhos:\n\n  ⌘K / Ctrl+K   Abrir comandos\n  ?              Ajuda\n  Esc            Fechar diálogo\n  ⌘+Click       Abrir em nova aba");
    },
  },
];


// ════════════════════════════════════════════════════════════════════════════
// Fuzzy match — score quanto melhor o match
// ════════════════════════════════════════════════════════════════════════════
export function scoreCommand(cmd: Command, query: string, label: string): number {
  if (!query.trim()) return 1;  // sem query, todos passam

  const q = query.toLowerCase().trim();
  const haystack = [
    label.toLowerCase(),
    ...(cmd.keywords ?? []).map(k => k.toLowerCase()),
  ].join(" | ");

  // Match exato no início → score alto
  if (haystack.startsWith(q)) return 100;
  // Match exato qualquer lugar
  if (haystack.includes(q)) return 50;
  // Subsequence match (cada char do query aparece em ordem)
  let qi = 0;
  for (const c of haystack) {
    if (c === q[qi]) qi++;
    if (qi === q.length) return 10;
  }
  return 0;
}
